import { Text } from '@billda/design-system';

export interface ProgressBarProps {
  percentage: number;
  size?: 'small' | 'medium' | 'large';
  variant?: 'fixed' | 'full-width';
}

/**
 * MUI theme palette path for the budget status colour.
 * Kept for legacy consumers outside this feature (grouped-view ProgressChip).
 */
export const getProgressColor = (value: number): string => {
  if (value < 100) {
    return 'success.main'; // Green - under budget
  }
  if (value === 100) {
    return 'warning.main'; // Amber - at budget
  }
  return 'error.main'; // Red - over budget
};

/** Budget status tone — green under budget, amber at, red over. */
export const getProgressTone = (
  value: number
): 'success' | 'warning' | 'error' => {
  if (value < 100) {
    return 'success';
  }
  if (value === 100) {
    return 'warning';
  }
  return 'error';
};

/** Semantic token classes for the budget status colour. */
export const progressToneClasses: Record<
  'success' | 'warning' | 'error',
  string
> = {
  success: 'text-content-success',
  warning: 'text-content-warning',
  error: 'text-content-error',
};

export const progressToneSurfaceClasses: Record<
  'success' | 'warning' | 'error',
  string
> = {
  success: 'bg-surface-success',
  warning: 'bg-surface-warning',
  error: 'bg-surface-error',
};

export function ProgressBar({
  percentage,
  size = 'small',
  variant = 'fixed',
}: ProgressBarProps) {
  const barHeight = size === 'small' ? 4 : size === 'medium' ? 6 : 8;
  const isFullWidth = variant === 'full-width';
  const fixedBarWidth = size === 'small' ? 70 : size === 'medium' ? 100 : 120;

  return (
    <div
      className={`flex items-center gap-2 ${isFullWidth ? 'w-full' : 'w-auto'}`}
    >
      {/* Background track */}
      <div
        className={`overflow-hidden rounded-lg bg-surface-disabled ${
          isFullWidth ? 'flex-1' : ''
        }`}
        style={{
          width: isFullWidth ? undefined : fixedBarWidth,
          height: barHeight,
        }}
      >
        {/* Progress fill */}
        <div
          className={`h-full transition-[width] duration-300 ${progressToneSurfaceClasses[getProgressTone(percentage)]}`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>

      {/* Percentage text */}
      <Text
        variant="caption"
        color="secondary"
        className="min-w-10 shrink-0 font-semibold text-xs"
      >
        {percentage.toFixed(1)}%
      </Text>
    </div>
  );
}
